import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { apiGet, apiPost } from '../../lib/api';
import { BUSINESS_TYPES, getIndustryTemplate } from '../../lib/industryTemplates';

export default function Onboarding() {
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const { role, business, session, refreshProfile, loading } = useAuth();
  const [form, setForm] = useState({ business_name: '', business_type: 'general', phone: '', city: '' });
  const [linkPhone, setLinkPhone] = useState(searchParams.get('phone') || '');
  const [linkOptions, setLinkOptions] = useState([]);
  const [selectedBusinessId, setSelectedBusinessId] = useState(searchParams.get('businessId') || '');
  const [busy, setBusy] = useState(false);
  const [checkingLink, setCheckingLink] = useState(false);
  const [checkingBusiness, setCheckingBusiness] = useState(false);
  const template = getIndustryTemplate(form.business_type);
  const addProviderMode = searchParams.get('addProvider') === '1';

  useEffect(() => {
    if (loading) return;
    if (!session) { nav('/login'); return; }
    if (role === 'super_admin') { nav('/admin'); return; }
    if (role === 'merchant_owner' && business) { nav('/app'); return; }
    if (role === 'merchant_owner' && !business) {
      setCheckingBusiness(true);
      refreshProfile()
        .then((data) => {
          if (data?.business) nav('/app', { replace: true });
        })
        .finally(() => setCheckingBusiness(false));
      return;
    }
    if (role === 'customer') {
      setCheckingLink(true);
      apiGet('/customer/providers')
        .then((res) => {
          if (!addProviderMode && (res.data || []).length > 0) nav('/my', { replace: true });
        })
        .catch(() => {})
        .finally(() => setCheckingLink(false));
    }
  }, [role, business, session, loading, nav, addProviderMode, refreshProfile]);

  const registerBiz = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await apiPost('/auth/merchant/register-business', form);
      await refreshProfile();
      toast.success('Business created');
      nav('/app');
    } catch (err) {
      toast.error(err.message);
    } finally { setBusy(false); }
  };

  const linkCustomer = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { phone: linkPhone };
      if (selectedBusinessId) payload.business_id = selectedBusinessId;
      const res = await apiPost('/customer/link-account', payload);
      await refreshProfile();
      const status = res.data?.status;
      if (status === 'pending_merchant_add' || status === 'merchant_not_registered') {
        toast.message(res.message || 'Request sent');
      } else {
        toast.success(res.message || 'Linked to your merchant');
      }
      nav('/my');
    } catch (err) {
      if ((err.message || '').includes('Multiple matching')) {
        try {
          const res = await apiGet(`/customer/link-options?phone=${encodeURIComponent(linkPhone)}`);
          const options = res.data || [];
          setLinkOptions(options);
          setSelectedBusinessId(options[0]?.business_id || '');
          toast.message('Choose your service provider');
        } catch (optionsErr) {
          toast.error(optionsErr.message);
        }
      } else {
        toast.error(err.message);
      }
    } finally { setBusy(false); }
  };

  const inputCls = 'w-full bg-white border border-gray-200 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-rozops-blue/20 focus:border-rozops-blue';

  if (loading || checkingLink || checkingBusiness) return <div className="min-h-screen grid place-items-center text-rozops-text-muted"><Loader2 className="animate-spin" /></div>;

  return (
    <div className="min-h-screen bg-rozops-bg flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-lg">
        <div className="flex justify-center mb-6">
          <img src="/ro-hlogo.png" alt="RozOps" className="h-16 w-auto object-contain" />
        </div>

        {role === 'merchant_owner' && !business && (
          <div className="bg-white rounded-3xl border border-gray-100 shadow-soft p-8">
            <h1 className="font-heading font-bold text-2xl text-rozops-navy">Set up your business</h1>
            <p className="text-sm text-rozops-text-muted mt-1">Tell us a bit about you. You can change this later.</p>
            <form onSubmit={registerBiz} className="mt-6 space-y-4">
              <div>
                <label className="block text-sm font-semibold text-rozops-text mb-1.5">Business name</label>
                <input required className={inputCls} value={form.business_name} onChange={(e) => setForm({ ...form, business_name: e.target.value })} placeholder={template.exampleBusiness} data-testid="onboard-biz-name" />
              </div>
              <div>
                <label className="block text-sm font-semibold text-rozops-text mb-1.5">Business type</label>
                <select className={inputCls} value={form.business_type} onChange={(e) => setForm({ ...form, business_type: e.target.value })}>
                  {BUSINESS_TYPES.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-semibold text-rozops-text mb-1.5">Phone</label>
                  <input className={inputCls} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-rozops-text mb-1.5">City</label>
                  <input className={inputCls} value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} />
                </div>
              </div>
              <button disabled={busy} data-testid="onboard-biz-submit" className="w-full bg-rozops-blue hover:bg-rozops-blue-dark text-white rounded-xl px-4 py-3 font-semibold shadow-soft transition-all disabled:opacity-50 inline-flex items-center justify-center gap-2">
                {busy && <Loader2 className="w-4 h-4 animate-spin" />} Continue
              </button>
            </form>
          </div>
        )}

        {role === 'customer' && (
          <div className="bg-white rounded-3xl border border-gray-100 shadow-soft p-8">
            <h1 className="font-heading font-bold text-2xl text-rozops-navy">{template.customerLinkTitle}</h1>
            <p className="text-sm text-rozops-text-muted mt-1">{template.customerLinkHelp}</p>
            <form onSubmit={linkCustomer} className="mt-6 space-y-4">
              <div>
                <label className="block text-sm font-semibold text-rozops-text mb-1.5">Service provider phone number</label>
                <input required className={inputCls} value={linkPhone} onChange={(e) => { setLinkPhone(e.target.value); setLinkOptions([]); setSelectedBusinessId(''); }} placeholder="Provider mobile number" data-testid="onboard-link-phone" />
              </div>
              {linkOptions.length > 0 && (
                <div>
                  <label className="block text-sm font-semibold text-rozops-text mb-1.5">Service provider</label>
                  <select required className={inputCls} value={selectedBusinessId} onChange={(e) => setSelectedBusinessId(e.target.value)} data-testid="onboard-link-provider">
                    {linkOptions.map(option => (
                      <option key={option.customer_id || option.business_id} value={option.business_id}>
                        {option.business?.business_name || 'Service provider'}{option.business?.business_type ? ` · ${option.business.business_type}` : ''}{option.area ? ` · ${option.area}` : ''}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              <button disabled={busy} data-testid="onboard-link-submit" className="w-full bg-rozops-blue hover:bg-rozops-blue-dark text-white rounded-xl px-4 py-3 font-semibold shadow-soft disabled:opacity-50 inline-flex items-center justify-center gap-2">
                {busy && <Loader2 className="w-4 h-4 animate-spin" />} Link account
              </button>
              <button type="button" onClick={() => nav('/my')} className="w-full text-rozops-text-muted text-sm">Skip for now</button>
            </form>
          </div>
        )}
      </div>
    </div>
  );
}
